import React, { forwardRef } from "react";
import QRCode from "react-qr-code";
import { server, qrBaseUrl } from "../../server";

// Utility function to convert numbers to words (INR)
const numberToWords = (num) => {
  if (!num || isNaN(num)) return "Zero Rupees Only";
  const a = [
    "",
    "One ",
    "Two ",
    "Three ",
    "Four ",
    "Five ",
    "Six ",
    "Seven ",
    "Eight ",
    "Nine ",
    "Ten ",
    "Eleven ",
    "Twelve ",
    "Thirteen ",
    "Fourteen ",
    "Fifteen ",
    "Sixteen ",
    "Seventeen ",
    "Eighteen ",
    "Nineteen ",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];

  const inWords = (n) => {
    if ((n = n.toString()).length > 9) return "Overflow";
    let nArr = ("000000000" + n)
      .substr(-9)
      .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
    if (!nArr) return "";
    let str = "";
    str +=
      nArr[1] != 0
        ? (a[Number(nArr[1])] || b[nArr[1][0]] + " " + a[nArr[1][1]]) + "Crore "
        : "";
    str +=
      nArr[2] != 0
        ? (a[Number(nArr[2])] || b[nArr[2][0]] + " " + a[nArr[2][1]]) + "Lakh "
        : "";
    str +=
      nArr[3] != 0
        ? (a[Number(nArr[3])] || b[nArr[3][0]] + " " + a[nArr[3][1]]) +
          "Thousand "
        : "";
    str +=
      nArr[4] != 0
        ? (a[Number(nArr[4])] || b[nArr[4][0]] + " " + a[nArr[4][1]]) +
          "Hundred "
        : "";
    str +=
      nArr[5] != 0
        ? (str != "" ? "and " : "") +
          (a[Number(nArr[5])] || b[nArr[5][0]] + " " + a[nArr[5][1]])
        : "";
    return str;
  };

  return `${inWords(Math.floor(num)).trim()} Rupees Only`;
};

const PrintableFeeReceipt = forwardRef(({ receipt }, ref) => {
  if (!receipt) return null;

  const {
    id: receiptId = 0,
    receiptNumber = "REC-2026-0001",
    receiptDate,
    totalAmountPaid = 0,
    paymentMode = "Cash",
    isDuplicateCopy = false,
    status = "Valid",
    uniqueId = null,
    student = {},
    feePayment = {},
  } = receipt;

  const studentFee = feePayment?.studentFee || {};
  const feeStructure = studentFee?.feeStructure || {};

  // Formatted date string
  const formattedDate = receiptDate
    ? new Date(receiptDate).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : new Date().toLocaleDateString("en-GB");

  // QR Code Verification Link — Public verification URL for Student Fee Receipt
  const qrVerificationUrl = `${qrBaseUrl}/verify/studentFee/${uniqueId || ""}`;

  const isCancelled = status === "Cancelled" || status === "cancelled";
  const isPaid = status === "Valid" && !isDuplicateCopy;

  return (
    <div
      ref={ref}
      className="w-[210mm] min-h-[148mm] p-7 bg-white text-slate-900 font-sans shadow-none print:shadow-none print:m-0 relative border-2 border-indigo-900/30 print:border-none select-none rounded-2xl"
    >
      {/* Duplicate copy watermark (only when valid) */}
      {isDuplicateCopy && !isCancelled && (
        <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none transform -rotate-12">
          <span className="text-8xl font-black uppercase text-rose-600 tracking-widest border-8 border-rose-600 p-6 rounded-3xl">
            DUPLICATE COPY
          </span>
        </div>
      )}

      {/* CANCELLED stamp */}
      {isCancelled && (
        <div className="absolute inset-0 flex items-center justify-center opacity-20 pointer-events-none transform -rotate-12 z-10">
          <span className="text-8xl font-black uppercase text-red-700 tracking-widest border-8 border-red-700 px-8 py-4 rounded-3xl">
            CANCELLED
          </span>
        </div>
      )}

      {/* PAID stamp (original valid receipts) */}
      {isPaid && (
        <div className="absolute top-6 right-6 pointer-events-none z-10 opacity-25 transform rotate-12">
          <span className="text-5xl font-black uppercase text-emerald-700 tracking-widest border-4 border-emerald-700 px-5 py-2 rounded-2xl">
            PAID
          </span>
        </div>
      )}

      {/* Header Container with Logo */}
      <div className="flex justify-between items-center border-b-2 border-indigo-900 pb-4 mb-4">
        <div className="flex items-center gap-4">
          {/* Logo Image */}
          <img
            src="/assets/images/favIcon.png"
            alt="Dee Novo Public School Logo"
            className="w-16 h-16 object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
              if (e.target.nextSibling)
                e.target.nextSibling.style.display = "flex";
            }}
          />
          <div className="w-16 h-16 bg-indigo-900 text-white font-black rounded-xl hidden items-center justify-center text-2xl shadow-sm">
            DNS
          </div>

          <div>
            <h1 className="text-2xl font-black uppercase text-indigo-950 tracking-wide">
              DEE NOVO PUBLIC SCHOOL
            </h1>
            <p className="text-xs text-slate-600 font-bold">
              Affiliated to CBSE Board • Main Campus, Knowledge Park
            </p>
            <p className="text-xs text-slate-500 font-medium">
              Phone: +91 98765 43210 | Email: accounts@deenovoschool.edu.in
            </p>
          </div>
        </div>

        <div className="text-right">
          <div className="flex flex-col items-end gap-1 mb-1">
            <span className="inline-block bg-indigo-950 text-white text-xs font-black px-3.5 py-1 rounded-full uppercase tracking-wider shadow-sm">
              OFFICIAL FEE RECEIPT
            </span>
            {isDuplicateCopy && (
              <span className="inline-block bg-rose-100 text-rose-800 border border-rose-300 text-[10px] font-black px-2.5 py-0.5 rounded-full uppercase tracking-wider shadow-xs">
                DUPLICATE COPY
              </span>
            )}
          </div>
          <p className="text-sm font-black text-slate-900">
            No: <span className="text-indigo-800">{receiptNumber}</span>
          </p>
          <p className="text-xs text-slate-500 font-bold">
            Date: {formattedDate}
          </p>
        </div>
      </div>

      {/* Student Profile & Fee Info Box */}
      <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 grid grid-cols-2 gap-y-2.5 text-xs mb-4">
        <div>
          <span className="text-slate-500 font-semibold">Student Name:</span>{" "}
          <strong className="text-slate-900 text-sm font-black">
            {student?.studentName || "N/A"}
          </strong>
        </div>
        <div>
          <span className="text-slate-500 font-semibold">
            Admission / Reg No:
          </span>{" "}
          <strong className="text-slate-900 font-extrabold">
            {student?.admissionNumber || "N/A"}
          </strong>
        </div>
        <div>
          <span className="text-slate-500 font-semibold">Class & Section:</span>{" "}
          <strong className="text-slate-900 font-extrabold">
            Class {student?.class || "N/A"}{" "}
            {student?.section ? `(${student.section})` : ""}
          </strong>
        </div>
        <div>
          <span className="text-slate-500 font-semibold">Roll Number:</span>{" "}
          <strong className="text-slate-900 font-extrabold">
            {student?.rollNumber || "N/A"}
          </strong>
        </div>
        <div>
          <span className="text-slate-500 font-semibold">Father's Name:</span>{" "}
          <strong className="text-slate-900 font-extrabold">
            {student?.fatherName || "N/A"}
          </strong>
        </div>
        <div>
          <span className="text-slate-500 font-semibold">Payment Mode:</span>{" "}
          <strong className="text-emerald-700 bg-emerald-100/80 px-2.5 py-0.5 rounded border border-emerald-300 font-black">
            {paymentMode}
          </strong>
        </div>
      </div>

      {/* Fee Itemized Table */}
      <table className="w-full text-left border-collapse mb-4 text-xs">
        <thead>
          <tr className="bg-indigo-950 text-white uppercase font-black tracking-wider">
            <th className="p-2.5 rounded-l-lg">S.NO</th>
            <th className="p-2.5">FEE HEAD DESCRIPTION</th>
            <th className="p-2.5">FEE MONTH / TERM</th>
            <th className="p-2.5 text-right">TOTAL FEE</th>
            <th className="p-2.5 text-right rounded-r-lg">PAID AMOUNT</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          <tr>
            <td className="p-2.5 font-bold text-slate-500">01</td>
            <td className="p-2.5 font-black text-slate-800">
              {feeStructure?.title || "Academic Tuition Fee"}
            </td>
            <td className="p-2.5 font-bold text-slate-600">
              {studentFee?.month || "Current Term"}
            </td>
            <td className="p-2.5 text-right font-bold text-slate-700">
              ₹{(studentFee?.totalAmount || totalAmountPaid).toLocaleString()}
            </td>
            <td className="p-2.5 text-right font-black text-emerald-700 text-sm">
              ₹{totalAmountPaid.toLocaleString()}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-indigo-950 bg-indigo-50/80 font-black">
            <td
              colSpan="4"
              className="p-2.5 text-right uppercase tracking-wider text-indigo-950"
            >
              TOTAL AMOUNT RECEIVED:
            </td>
            <td className="p-2.5 text-right text-base text-indigo-950 font-black">
              ₹{totalAmountPaid.toLocaleString()}
            </td>
          </tr>
        </tfoot>
      </table>

      {/* Amount in Words */}
      <div className="bg-indigo-50/60 p-3 rounded-xl border border-indigo-100 mb-6 text-xs flex justify-between items-center">
        <div>
          <span className="text-indigo-950 font-black uppercase tracking-wider mr-2">
            AMOUNT IN WORDS:
          </span>
          <span className="font-black text-indigo-950 italic text-sm">
            {numberToWords(totalAmountPaid)}
          </span>
        </div>
      </div>

      {/* QR Code & Official Signatures Footer */}
      <div className="flex justify-between items-end border-t-2 border-dashed border-slate-300 pt-4">
        {/* QR Code Verification Section */}
        <div className="flex items-center gap-3">
          <div className="p-1.5 bg-white border border-slate-300 rounded-xl shadow-xs flex flex-col items-center">
            <QRCode value={qrVerificationUrl} size={54} />
          </div>
          <div>
            <div className="text-[10px] font-black text-indigo-950 uppercase tracking-wider">
              QR Verified Receipt
            </div>
            {uniqueId && (
              <div className="text-[8px] text-slate-400 font-mono mt-0.5 max-w-[130px] break-all leading-tight">
                UID: {uniqueId}
              </div>
            )}
            <div className="text-[9px] text-slate-500 font-semibold mt-0.5">
              Scan to verify digital authenticity
            </div>
          </div>
        </div>

        {/* Signatures */}
        <div className="flex gap-10 text-center text-xs">
          <div>
            <div className="w-32 border-b border-slate-400 mb-1"></div>
            <p className="text-slate-500 font-bold">Depositor / Parent</p>
          </div>
          <div>
            <div className="w-36 border-b-2 border-indigo-950 mb-1"></div>
            <p className="font-black text-indigo-950">Accounts Officer</p>
            <p className="text-[10px] text-slate-400 font-semibold">
              Dee Novo Public School
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

export default PrintableFeeReceipt;
